import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { replace, useLoaderData, useNavigate } from 'react-router-dom'
import { useClerk, useUser } from '@clerk/clerk-react'
import './add.css'

// COMPONENTS
import AnchorLink from '@components/atoms/anchor-link'
import Button from '@components/atoms/button'
import OneTimePassword from '@components/molecules/one-time-password'
import Spinner from '@components/atoms/spinner'
import TextField from '@components/atoms/textfield'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { useInput } from '@hooks/inputs'
import { getErrorMessage } from '@utils/error-helper'
import { formatInput, normalizeInput } from '@utils/phone-number-helper'
import { createAuthenticatedLoader } from '@utils/loaders'
import { getPendingPhoneNumber, getVerifiedPhoneNumber } from '@utils/user-onboarding'
import { isNonEmptyString } from '@utils/type-checkers'
import { useProfilePhoneContext } from '..'


interface LoaderData {
    pendingPhoneId?: string
    pendingPhoneNumber?: string
}

const disableDuration = 30
const formId = 'profile-phone-form'
const otpLength = 6
const placeholder = '+1 (555) 555-5555'
const maxPhoneLength = placeholder.length

function isPhoneAlreadyUsedError(error: unknown): boolean {
    if (typeof error !== 'object' || error == null || !('errors' in error)) return false
    const errors = (error as { errors?: Array<{ code?: string }> }).errors
    return Array.isArray(errors) && errors[0]?.code === 'form_identifier_exists'
}

const Component: FC = () => {
    const { pendingPhoneId, pendingPhoneNumber } = useLoaderData() as LoaderData
    const { signOut } = useClerk()
    const { user } = useUser()
    const navigate = useNavigate()
    const { setToast } = useProfilePhoneContext()
    const [phoneNumber, phoneNumberProps, setPhoneNumber] = useInput({
        initValue: pendingPhoneNumber ? formatInput(pendingPhoneNumber) : '',
        onChange: (value) => formatInput(value, phoneNumber)
    })
    const [otp, otpProps, setOtp] = useInput()
    const [currentPhoneId, setCurrentPhoneId] = useState(pendingPhoneId || '')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSendingCode, setIsSendingCode] = useState(false)
    const [isPhoneTaken, setIsPhoneTaken] = useState(false)
    const [phoneTakenNumber, setPhoneTakenNumber] = useState('')
    const [currentCountDown, setCurrentCountDown] = useState(disableDuration)
    const [otpSubmitEnabled, setOtpSubmitEnabled] = useState(false)
    const dialogRef = useRef<HTMLDialogElement>(null)
    const spinnerDialogRef = useRef<HTMLDialogElement>(null)
    const autoPreparedPhoneIdsRef = useRef<Set<string>>(new Set())

    const isVerifyStep = isNonEmptyString(currentPhoneId)

    useEffect(() => {
        if (!dialogRef.current) return

        if (isPhoneTaken && !dialogRef.current.open) dialogRef.current.showModal()
        else if (!isPhoneTaken && dialogRef.current.open) dialogRef.current.close()
    }, [isPhoneTaken])

    useEffect(() => {
        if (!spinnerDialogRef.current) return

        if (isSubmitting && !spinnerDialogRef.current.open) spinnerDialogRef.current.showModal()
        else if (!isSubmitting && spinnerDialogRef.current.open) spinnerDialogRef.current.close()
    }, [isSubmitting])

    const sendVerificationCode = useCallback(async (phoneId: string, showSuccessToast = false) => {
        try {
            const resource = user?.phoneNumbers.find(({ id }) => id === phoneId)
            if (!resource || resource.verification.status === 'verified') return

            setCurrentCountDown(disableDuration)
            setOtpSubmitEnabled(false)
            setIsSendingCode(true)
            await resource.prepareVerification()
            if (showSuccessToast) setToast({ type: 'success', text: 'Code sent.' })
        } catch (error: unknown) {
            console.error(error)
            setToast({
                type: 'error',
                text: getErrorMessage(error, 'Failed to send verification code.')
            })
        } finally {
            setIsSendingCode(false)
        }
    }, [setToast, user])

    useEffect(() => {
        if (!currentPhoneId) return
        if (autoPreparedPhoneIdsRef.current.has(currentPhoneId)) return

        autoPreparedPhoneIdsRef.current.add(currentPhoneId)
        void sendVerificationCode(currentPhoneId)
    }, [currentPhoneId, sendVerificationCode])

    useEffect(() => {
        if (!isVerifyStep) return

        if (currentCountDown <= 0) {
            setOtpSubmitEnabled(true)
            return
        }

        const intervalId = window.setInterval(() => {
            setCurrentCountDown((prevCount) => prevCount - 1)
        }, 1000)

        return () => window.clearInterval(intervalId)
    }, [currentCountDown, isVerifyStep])

    const submittable = useMemo(() => {
        if (isVerifyStep) return otp.length === otpLength
        return normalizeInput(phoneNumber).length === 10
    }, [isVerifyStep, otp, phoneNumber])

    async function handleAddPhone(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()
            if (!user) throw new Error('Auth services have yet to load.')

            setIsSubmitting(true)
            const normalizedPhoneNumber = `+1${normalizeInput(phoneNumber)}`
            const resource = await user.createPhoneNumber({ phoneNumber: normalizedPhoneNumber })
            setCurrentPhoneId(resource.id)
            setOtp('')
        } catch (error: unknown) {
            console.error(error)
            if (isPhoneAlreadyUsedError(error)) {
                setPhoneTakenNumber(formatInput(phoneNumber))
                setIsPhoneTaken(true)
                return
            }

            setToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to add your phone number.')
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleVerifyPhone(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()

            const resource = user?.phoneNumbers.find(({ id }) => id === currentPhoneId)
            if (!user || !resource) throw new Error('Phone number is no longer available.')
            if (!isNonEmptyString(otp)) throw new Error('Enter the 6-digit verification code.')

            setIsSubmitting(true)
            const result = await resource.attemptVerification({ code: otp })
            if (result.verification.status !== 'verified') throw new Error(`Verification failed with status: ${result.verification.status}`)

            if (user.primaryPhoneNumberId !== result.id) await user.update({ primaryPhoneNumberId: result.id })
            navigate('/account/profile', { replace: true })
        } catch (error: unknown) {
            console.error(error)
            setToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to verify your phone number.')
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleUseDifferentNumber() {
        const resource = user?.phoneNumbers.find(({ id }) => id === currentPhoneId)
        const previousPhoneId = currentPhoneId
        const previousPhoneNumber = phoneNumber

        setCurrentPhoneId('')
        setOtp('')
        setPhoneNumber('')
        setCurrentCountDown(disableDuration)
        setOtpSubmitEnabled(false)

        try {
            if (!resource) return

            await resource.destroy()
            await user?.reload()
        } catch (error) {
            console.error(error)
            autoPreparedPhoneIdsRef.current.delete(previousPhoneId)
            setCurrentPhoneId(previousPhoneId)
            setPhoneNumber(previousPhoneNumber)
            setToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to reset your phone number.')
            })
        }
    }

    async function handleResendCode() {
        if (!currentPhoneId) return
        await sendVerificationCode(currentPhoneId, true)
    }

    async function handleSignInInstead() {
        try {
            await signOut()
            navigate('/sign-in', {
                replace: true,
                state: { initialView: 'form', intent: 'sign-in' }
            })
        } catch (error) {
            console.error(error)
            setToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to sign out and switch accounts.')
            })
        }
    }

    return (<>
        {!isVerifyStep && (
            <div className='flex flex-col items-center gap-2 text-center'>
                <h4>Add Phone Number</h4>
                <p>We need a phone number on your profile for account access and notifications.</p>
            </div>
        )}

        {isVerifyStep && (
            <div className='flex flex-col items-center gap-2 text-center'>
                <h4>Enter Your Verification Code</h4>
            </div>
        )}

        {!isVerifyStep && (
            <form id={formId} onSubmit={handleAddPhone} className='flex flex-col' style={{ gap: 'inherit' }}>
                <TextField
                    {...phoneNumberProps}
                    type='tel'
                    autoComplete='tel'
                    autoCapitalize='off'
                    autoCorrect='off'
                    inputMode='tel'
                    name='phone-number'
                    maxLength={maxPhoneLength}
                    placeholder='Phone Number'
                    divideElementLeft={true}
                    inlineElementLeft={<p><strong>+1</strong></p>}
                    required
                />
                <Button type='submit' theme='primary' disabled={!submittable || isSubmitting || isSendingCode}>
                    {isSubmitting ? 'Sending Code...' : 'Continue'}
                </Button>
            </form>
        )}

        {isVerifyStep && (
            <div className='flex flex-col' style={{ gap: 'inherit' }}>
                <form id={formId} onSubmit={handleVerifyPhone} className='flex flex-col' style={{ gap: 'inherit' }}>
                    <OneTimePassword className='profile-phone-otp' onChange={otpProps.onChange} value={otp} />
                    <Button type='submit' theme='primary' disabled={!submittable || isSubmitting || isSendingCode}>
                        {isSubmitting ? 'Verifying...' : 'Continue'}
                    </Button>
                </form>

                <p style={{ alignSelf: 'center' }}>
                    {isSendingCode
                        ? <span>Sending code...</span>
                        : !otpSubmitEnabled
                            ? <span>{`Resend in ${currentCountDown}s`}</span>
                            : <AnchorLink onClick={handleResendCode}>Resend Code</AnchorLink>
                    }
                </p>

                <small style={{ alignSelf: 'center' }}>
                    <button
                        type='button'
                        className='text-gray-500'
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit' }}
                        onClick={handleUseDifferentNumber}
                        disabled={isSendingCode || isSubmitting}
                    >
                        Use a different number
                    </button>
                </small>
            </div>
        )}

        <dialog ref={dialogRef}>
            <div>
                <div className='flex flex-col items-center justify-center text-center' style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1.5rem', borderRadius: 'var(--border-radius)', overflowY: 'auto', maxWidth: '22rem' }}>
                    <h3>This phone number is already in use</h3>
                    <p>
                        {phoneTakenNumber ? `${phoneTakenNumber} is already attached to an existing account.` : 'This phone number is already attached to an existing account.'}
                    </p>
                    <p>Please sign in with that phone number or enter a different one to continue.</p>
                    <div className='flex flex-col' style={{ gap: '0.75rem', width: '100%' }}>
                        <Button type='button' theme='primary' onClick={handleSignInInstead}>
                            Sign In Instead
                        </Button>
                        <Button type='button' theme='secondary' onClick={() => setIsPhoneTaken(false)}>
                            Use Different Number
                        </Button>
                    </div>
                </div>
            </div>
        </dialog>

        <dialog ref={spinnerDialogRef}>
            <div>
                <div className='flex flex-col items-center justify-center' style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

const loader = createAuthenticatedLoader(async (_, Clerk) => {
    if (getVerifiedPhoneNumber(Clerk.user)) return replace('/account/profile')

    const pendingPhone = getPendingPhoneNumber(Clerk.user)
    return {
        pendingPhoneId: pendingPhone?.id,
        pendingPhoneNumber: pendingPhone?.phoneNumber
    } satisfies LoaderData
})

const AddPhoneRoute: RouteObject = {
    index: true,
    Component,
    loader,
}

export { AddPhoneRoute }
